import React, { Fragment, useEffect, useRef, useState } from "react";
import CustomerInspection from "./CustomerInspection";
import { lineItemRequestTypeLabel, lineItemRefundMethod } from "./lineItemRequestTypeLabel";
import { customerPayments, customerPaymentUrl } from "./customerPayments";
import { isWarrantyTimeline, warrantyTimelineSteps, progressedShippingDescription } from "./warrantyTimeline";
import { PickupAddress } from "components/v2/Common/Address";
import { StyledExchange } from "components/v2/ReturnCart/styled";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getEncipherencode } from "utils/encipherencode";
import {
  getS3SignedUrls,
  getPickupSignedUrls,
  paymentStatus,
  resubmitReturnImageQC,
  selectedReturnOrderAPIV2,
  updateShippingDetails,
} from "utils/api";
import { getReturnOrder } from "features/orders/orderSlice";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Grid,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import styled from "styled-components";
import LineItemCard from "components/v2/Common/LineItemCard";
import FloatingCloseButton from "components/v2/Common/FloatingCloseButton";
import BottomSheetDrawer from "components/v2/Common/BottomSheetDrawer";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { format } from "date-fns";
import {
  StyledApp,
  StyledTimelineDrawer,
  StyledTracking,
} from "components/v2/Tracking/styled";
import { getEmail, getPhone } from "features/user/userSlice";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReturnDetails from "components/v2/Common/ReturnDetails";
import { StyledRefundDetails } from "components/v2/Common/ReturnDetails/styled";
import { LazyLoadImage } from "react-lazy-load-image-component";
import LoadingError from "components/v2/Common/LoadingError";
import {
  hasValidTrackingReference,
  stripVersionPrefix,
  versionedPath,
} from "utils/routes";
import { V2TrackingBoneyardLoader } from "components/v2/Loaders/BoneyardLoaders";
import PageHeader from "components/v2/Common/PageHeader";
import PageIntro from "components/v2/Common/PageIntro";
import { hasExchangeFulfillmentRequestType } from "utils/returnTypes";
import { getApiErrorMessage } from "utils/apiError";
import { buildReturnMediaPresignFiles } from "utils/returnMedia";
import {
  buildReturnImageQCResubmissionPayload,
  clearReturnImageQCUploadedMediaIds,
  findReturnImageQCUploadAlert,
  getReturnImageQCFailureMessages,
  getReturnImageQCLineItemId,
  getReturnImageQCMissingChecks,
  isReturnImageQCAlertForLineItem,
  isReturnImageQCUploadAlert,
  markReturnImageQCLineItemPending,
  RETURN_IMAGE_QC_MAX_IMAGES,
  validateReturnImageQCFile,
} from "utils/returnImageQC";

const sanitizeUploadFilename = (filename) => {
  const rawName = String(filename || "")
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .trim();
  const normalizedName = rawName
    .normalize("NFKD")
    .split("")
    .filter((char) => char.charCodeAt(0) <= 127)
    .join("");
  const safeName = normalizedName
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[._-]+|[._-]+$/g, "");

  if (!safeName) {
    return `upload_${Date.now()}`;
  }

  const extensionIndex = safeName.lastIndexOf(".");
  const hasExtension = extensionIndex > 0;
  const stem = (hasExtension ? safeName.slice(0, extensionIndex) : safeName)
    .slice(0, 100)
    .replace(/^[._-]+|[._-]+$/g, "") || "upload";
  const extension = hasExtension
    ? safeName
        .slice(extensionIndex)
        .toLowerCase()
        .replace(/[^A-Za-z0-9.]/g, "")
        .slice(0, 16)
    : "";

  return `${stem}${extension}`;
};

const APPLICABLE_TIMELINES = [
  { code: "T0", label: "Pending" },
  { code: "T1", label: "Accepted" },
  { code: "T2", label: "Rejected" },
  { code: "T3", label: "Shipment Generated" },
  { code: "T4", label: "Picked Up" },
  { code: "T5", label: "In Transit" },
  { code: "T6", label: "Out for Delivery" },
  { code: "T7", label: "Delivered" },
  { code: "T8", label: "QC Inspected" },
  { code: "T9", label: "Refund Initiated" },
  { code: "T10", label: "Exchange Initiated" },
];

function Tracking() {
  const location = useLocation();
  const currentPath = stripVersionPrefix(location.pathname);
  const merchantConfig = useSelector(getMerchantConfig);
  const selectedReturnRequestId = useSelector(getReturnOrder);
  const [returnItems, setReturnItems] = useState({});
  const shippingDetailsRef = useRef(null);
  const paymentDetailsRef = useRef(null);
  const paymentDetailRefs = useRef({});
  const [uploads, setUploads] = useState(null);
  const [preview, setPreview] = useState(null);
  const [addressCopied, setAddressCopied] = useState(false);
  const [awbNumber, setAwbNumber] = useState(null);
  const [courierName, setCourierName] = useState(null);
  const [trackingLink, setTrackingLink] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [submittingTrackingDetails, setSubmittingTrackingDetails] =
    useState(false);
  const [trackingDetailsSubmitted, setTrackingDetailsSubmitted] =
    useState(false);
  const [trackingDetailsSuccessMessage, setTrackingDetailsSuccessMessage] =
    useState("");
  const [trackingDetailsErrorMessage, setTrackingDetailsErrorMessage] =
    useState("");
  const [pickupProofFilesByItem, setPickupProofFilesByItem] = useState({});
  const [pickupProofPreviewsByItem, setPickupProofPreviewsByItem] = useState(
    {},
  );
  const [pickupProofUploadingByItem, setPickupProofUploadingByItem] = useState(
    {},
  );
  const [pickupProofSuccessByItem, setPickupProofSuccessByItem] = useState({});
  const [pickupProofErrorByItem, setPickupProofErrorByItem] = useState({});
  const pickupProofPreviewsRef = useRef(pickupProofPreviewsByItem);
  const [imageQCFilesByItem, setImageQCFilesByItem] = useState({});
  const [imageQCPreviewsByItem, setImageQCPreviewsByItem] = useState({});
  const [imageQCUploadingByItem, setImageQCUploadingByItem] = useState({});
  const [imageQCSuccessByItem, setImageQCSuccessByItem] = useState({});
  const [imageQCErrorByItem, setImageQCErrorByItem] = useState({});
  const [imageQCUploadedMediaIdsByItem, setImageQCUploadedMediaIdsByItem] =
    useState({});
  const imageQCPreviewsRef = useRef(imageQCPreviewsByItem);
  const imageQCUploadRefs = useRef({});
  const rmsConfig = useSelector(getRmsConfig);
  const [returnItemsLoader, setReturnItemsLoader] = useState(false);
  const [timelineItem, setTimelineItem] = useState(null);
  const [showShipGuide, setShowShipGuide] = useState(false);
  const navigate = useNavigate();
  const userPhone = useSelector(getPhone);
  const userEmail = useSelector(getEmail);
  const [apiErrorLoading, setApiErrorLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const encipherencode = getEncipherencode(searchParams);
  const trackingReferenceId = searchParams.get("rid");
  const validTrackingReferenceId = hasValidTrackingReference(
    trackingReferenceId,
  )
    ? String(trackingReferenceId).trim()
    : null;
  const requestIdParam = searchParams.get("request_id");
  const validRequestIdParam = hasValidTrackingReference(requestIdParam)
    ? requestIdParam
    : null;
  const validSelectedReturnRequestId = hasValidTrackingReference(
    selectedReturnRequestId,
  )
    ? selectedReturnRequestId
    : null;
  const backTo = searchParams.get("back_to");
  const paymentTransactionId =
    searchParams.get("razorpay_invoice_id") || searchParams.get("txn_id");
  const paymentOrderId = searchParams.get("order_id");
  const shouldCreateRequest = searchParams.get("cr") || true;

  const fetchReturnRequest = (merchantId, returnRequestId) => {
    if (merchantId && merchantId !== null) {
      selectedReturnOrderAPIV2(
        merchantId,
        returnRequestId,
        validTrackingReferenceId,
      )
        .then((resp) => {
          setReturnItems(resp.data);
          setReturnItemsLoader(false);
        })
        .catch((err) => {
          if (userPhone === "" && userEmail === "") {
            let url = "/";
            if (encipherencode) {
              url += `?encipherencode=${encipherencode}`;
            }
            navigate(url);
          } else {
            setApiErrorLoading(true);
          }
        });
    }
  };

  useEffect(() => {
    setReturnItemsLoader(true);
    if (
      !paymentTransactionId &&
      !validRequestIdParam &&
      !validSelectedReturnRequestId &&
      !validTrackingReferenceId
    ) {
      setReturnItemsLoader(false);
      navigate(versionedPath("/orders", location.pathname), { replace: true });
      return;
    }

    if (
      merchantConfig &&
      merchantConfig !== null &&
      Object.keys(merchantConfig).length > 0 &&
      merchantConfig.merchant_id !== null
    ) {
      if (paymentTransactionId) {
        paymentStatus(
          merchantConfig.merchant_id,
          paymentTransactionId,
          validTrackingReferenceId,
          paymentOrderId,
          false,
          shouldCreateRequest,
        ).then((resp) => {
          const currentAccessToken = sessionStorage.getItem("access_token");
          if (!currentAccessToken) {
            sessionStorage.setItem("access_token", resp.data.access_token);
          }
          fetchReturnRequest(
            merchantConfig.merchant_id,
            validRequestIdParam || validSelectedReturnRequestId,
          );
        });
      } else {
        if (validTrackingReferenceId) {
          fetchReturnRequest(merchantConfig.merchant_id, null);
        } else if (
          validRequestIdParam ||
          validSelectedReturnRequestId
        ) {
          fetchReturnRequest(
            merchantConfig.merchant_id,
            validRequestIdParam || validSelectedReturnRequestId,
          );
        }
      }
    }

  }, [merchantConfig]);

  useEffect(() => {
    if (
      returnItems !== undefined &&
      returnItems.self_ship &&
      returnItems.self_ship !== null
    ) {
      if (returnItems.self_ship.awb_number !== null) {
        setDisabled(true);
        setTrackingDetailsSubmitted(true);
        setPreview(
          `${rmsConfig.s3_url}${returnItems.self_ship.consignment_image}`,
        );
      } else {
        setDisabled(false);
        setTrackingDetailsSubmitted(false);
      }
      setCourierName(returnItems.self_ship.courier_company);
      setTrackingLink(returnItems.self_ship.tracking_link);
      setAwbNumber(returnItems.self_ship.awb_number);
    }
  }, [returnItems]);

  useEffect(() => {
    pickupProofPreviewsRef.current = pickupProofPreviewsByItem;
  }, [pickupProofPreviewsByItem]);

  useEffect(() => {
    imageQCPreviewsRef.current = imageQCPreviewsByItem;
  }, [imageQCPreviewsByItem]);

  useEffect(() => {
    return () => {
      Object.values(pickupProofPreviewsRef.current).forEach((previews) => {
        previews.forEach((file) => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
      });
      Object.values(imageQCPreviewsRef.current).forEach((previews) => {
        previews.forEach((file) => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
      });
    };
  }, []);

  const updatedAddress = {
    addressLine1: returnItems.customer_address_line_1,
    addressLine2: returnItems.customer_address_line_2,
    pincode: returnItems.customer_address_pin,
    city: returnItems.customer_address_city,
    state: returnItems.customer_address_state,
  };

  const [vertical, setVertical] = useState(
    window.innerWidth < 600 ? true : false,
  );

  window.addEventListener("resize", () => {
    if (window.innerWidth < 600) {
      setVertical(true);
    } else {
      setVertical(false);
    }
  });

  const ColorlibConnector = styled(StepConnector)(() => ({
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: merchantConfig.primary_theme_color,
        border: !vertical
          ? `1px solid ${merchantConfig.primary_theme_color}`
          : "",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: merchantConfig.primary_theme_color,
        border: !vertical
          ? `1px solid ${merchantConfig.primary_theme_color}`
          : "",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      border: !vertical ? "1px dashed #5C5C5C" : "",
    },
  }));

  const submitHandler = async (e) => {
    e.preventDefault();
    setSubmittingTrackingDetails(true);
    setTrackingDetailsErrorMessage("");
    setTrackingDetailsSuccessMessage("");

    const formData = new FormData();
    formData.append("file", uploads);
    formData.append("merchant_id", merchantConfig.merchant_id);
    formData.append("return_request_id", returnItems.id);
    formData.append("awb_number", awbNumber);
    formData.append("tracking_link", trackingLink);
    formData.append("courier_company", courierName);

    try {
      await updateShippingDetails(formData);
      setDisabled(true);
      setTrackingDetailsSubmitted(true);
      setTrackingDetailsSuccessMessage(
        "Tracking details submitted successfully.",
      );
      setReturnItems((prevItems) => ({
        ...prevItems,
        status: "Customer shipment allocated",
        line_items: Array.isArray(prevItems.line_items)
          ? prevItems.line_items.map((item) =>
              isAwaitingTrackingDetails(item)
                ? markTrackingDetailsSubmitted(item)
                : item,
            )
          : prevItems.line_items,
        alerts: Array.isArray(prevItems.alerts)
          ? prevItems.alerts.map((alert) =>
              alert?.cta?.target === "self_ship_details"
                ? { ...alert, is_active: false, status: "resolved" }
                : alert,
            )
          : prevItems.alerts,
      }));
    } catch (error) {
      setTrackingDetailsErrorMessage(
        getApiErrorMessage(
          error,
          "Unable to submit tracking details. Please try again.",
        ),
      );
    } finally {
      setSubmittingTrackingDetails(false);
    }
  };

  const handleUploads = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (preview && String(preview).startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setUploads(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const getPickupProofItemKey = (item = {}, index = 0) =>
    String(
      item.return_request_line_item_id ||
        item.id ||
        item.line_item_id ||
        [item.product_id, item.variant_id, item.product_sku]
          .filter(Boolean)
          .join("-") ||
        index,
    );

  const getPickupProofLineItemId = (item = {}, index = 0) =>
    item.line_item_id ||
    item.id ||
    item.return_request_line_item_id ||
    getPickupProofItemKey(item, index);

  const handlePickupProofFiles = (itemKey, event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) {
      return;
    }

    setPickupProofFilesByItem((prev) => ({
      ...prev,
      [itemKey]: [...(prev[itemKey] || []), ...selectedFiles],
    }));
    setPickupProofPreviewsByItem((prev) => ({
      ...prev,
      [itemKey]: [
        ...(prev[itemKey] || []),
        ...selectedFiles.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          preview: URL.createObjectURL(file),
          url: "",
        })),
      ],
    }));
    setPickupProofSuccessByItem((prev) => ({ ...prev, [itemKey]: "" }));
    setPickupProofErrorByItem((prev) => ({ ...prev, [itemKey]: "" }));
    event.target.value = "";
  };

  const removePickupProofFile = (itemKey, fileIndex) => {
    setPickupProofFilesByItem((prev) => ({
      ...prev,
      [itemKey]: (prev[itemKey] || []).filter(
        (_, index) => index !== fileIndex,
      ),
    }));
    setPickupProofPreviewsByItem((prev) => {
      const removedPreview = prev[itemKey]?.[fileIndex]?.preview;
      if (removedPreview) {
        URL.revokeObjectURL(removedPreview);
      }

      return {
        ...prev,
        [itemKey]: (prev[itemKey] || []).filter(
          (_, index) => index !== fileIndex,
        ),
      };
    });
  };

  const handleImageQCFiles = (itemKey, event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    if (selectedFiles.length === 0) {
      return;
    }

    const validationError = selectedFiles
      .map(validateReturnImageQCFile)
      .find(Boolean);
    if (validationError) {
      setImageQCErrorByItem((prev) => ({
        ...prev,
        [itemKey]: validationError,
      }));
      return;
    }

    const currentFiles = imageQCFilesByItem[itemKey] || [];
    const existingKeys = new Set(
      currentFiles.map(
        (file) => `${file.name}:${file.size}:${file.lastModified}`,
      ),
    );
    const newFiles = selectedFiles.filter(
      (file) =>
        !existingKeys.has(`${file.name}:${file.size}:${file.lastModified}`),
    );
    const nextFiles = [...currentFiles, ...newFiles];

    if (nextFiles.length > RETURN_IMAGE_QC_MAX_IMAGES) {
      setImageQCErrorByItem((prev) => ({
        ...prev,
        [itemKey]: `You can submit up to ${RETURN_IMAGE_QC_MAX_IMAGES} images in one attempt.`,
      }));
      return;
    }

    setImageQCFilesByItem((prev) => ({ ...prev, [itemKey]: nextFiles }));
    setImageQCPreviewsByItem((prev) => ({
      ...prev,
      [itemKey]: [
        ...(prev[itemKey] || []),
        ...newFiles.map((file) => ({
          name: file.name,
          preview: URL.createObjectURL(file),
        })),
      ],
    }));
    setImageQCUploadedMediaIdsByItem((prev) =>
      clearReturnImageQCUploadedMediaIds(prev, itemKey),
    );
    setImageQCSuccessByItem((prev) => ({ ...prev, [itemKey]: "" }));
    setImageQCErrorByItem((prev) => ({ ...prev, [itemKey]: "" }));
  };

  const removeImageQCFile = (itemKey, fileIndex) => {
    setImageQCFilesByItem((prev) => ({
      ...prev,
      [itemKey]: (prev[itemKey] || []).filter(
        (_, index) => index !== fileIndex,
      ),
    }));
    setImageQCPreviewsByItem((prev) => {
      const removedPreview = prev[itemKey]?.[fileIndex]?.preview;
      if (removedPreview) {
        URL.revokeObjectURL(removedPreview);
      }

      return {
        ...prev,
        [itemKey]: (prev[itemKey] || []).filter(
          (_, index) => index !== fileIndex,
        ),
      };
    });
    setImageQCUploadedMediaIdsByItem((prev) => ({
      ...prev,
      [itemKey]: [],
    }));
    setImageQCErrorByItem((prev) => ({ ...prev, [itemKey]: "" }));
  };

  async function uploadMediaToS3(file, postData) {
    const formData = new FormData();
    Object.entries(postData.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    const response = await fetch(postData.url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} ${text}`);
    }
  }

  const submitPickupProof = async (item, itemKey, itemIndex) => {
    const fileList = pickupProofFilesByItem[itemKey] || [];
    if (fileList.length === 0 || pickupProofUploadingByItem[itemKey]) {
      return;
    }

    setPickupProofUploadingByItem((prev) => ({ ...prev, [itemKey]: true }));
    setPickupProofErrorByItem((prev) => ({ ...prev, [itemKey]: "" }));
    setPickupProofSuccessByItem((prev) => ({ ...prev, [itemKey]: "" }));

    try {
      const signedUploads = fileList.map((file, index) => ({
        file,
        requestName: `${index}_${sanitizeUploadFilename(file.name)}`,
      }));

      const filesMapping = signedUploads.map(({ file, requestName }) => ({
        name: requestName,
        content_type: file.type,
      }));
      const resp = await getPickupSignedUrls(
        item.return_shipment_awb || returnItems.return_shipment_awb || awbNumber,
        filesMapping,
        returnItems.order_id,
        returnItems.order_number,
        returnItems.id,
        getPickupProofLineItemId(item, itemIndex),
      );
      const postMap = (resp.data.posts || []).reduce((acc, post) => {
        acc[post.name] = post.post_data;
        return acc;
      }, {});
      const urlMap = (resp.data.posts || []).reduce((acc, post) => {
        acc[post.name] = post.file_url;
        return acc;
      }, {});

      await Promise.all(
        signedUploads.map(({ file, requestName }) => {
          const postData = postMap[requestName];
          if (!postData) {
            throw new Error(`No presigned URL for ${file.name}`);
          }

          return uploadMediaToS3(file, postData).then(() => {
            setPickupProofPreviewsByItem((prev) => ({
              ...prev,
              [itemKey]: (prev[itemKey] || []).map((preview) =>
                preview.name === file.name
                  ? { ...preview, url: urlMap[requestName] || "" }
                  : preview,
              ),
            }));
          });
        }),
      );

      setPickupProofSuccessByItem((prev) => ({
        ...prev,
        [itemKey]: "Pickup proof uploaded successfully.",
      }));
      setReturnItems((prevItems) => ({
        ...prevItems,
        line_items: Array.isArray(prevItems.line_items)
          ? prevItems.line_items.map((lineItem, index) =>
              getPickupProofItemKey(lineItem, index) === itemKey
                ? { ...lineItem, pickup_image_submission: false }
                : lineItem,
            )
          : prevItems.line_items,
      }));
    } catch (error) {
      setPickupProofErrorByItem((prev) => ({
        ...prev,
        [itemKey]: getApiErrorMessage(
          error,
          "Unable to upload pickup proof. Please try again.",
        ),
      }));
    } finally {
      setPickupProofUploadingByItem((prev) => ({
        ...prev,
        [itemKey]: false,
      }));
    }
  };

  const submitAdditionalQCImages = async (item, itemKey) => {
    const fileList = imageQCFilesByItem[itemKey] || [];
    if (fileList.length === 0 || imageQCUploadingByItem[itemKey]) {
      return;
    }

    setImageQCUploadingByItem((prev) => ({ ...prev, [itemKey]: true }));
    setImageQCErrorByItem((prev) => ({ ...prev, [itemKey]: "" }));
    setImageQCSuccessByItem((prev) => ({ ...prev, [itemKey]: "" }));

    try {
      const imageQCAlert = findReturnImageQCUploadAlert(
        returnItems.alerts || [],
        item,
      );
      const lineItemId = getReturnImageQCLineItemId(imageQCAlert, item);
      let uploadedMediaIds = imageQCUploadedMediaIdsByItem[itemKey] || [];

      if (uploadedMediaIds.length === 0) {
        const signedUploads = fileList.map((file, index) => ({
          file,
          requestName: `${index}_${sanitizeUploadFilename(file.name)}`,
        }));
        const filesMapping = buildReturnMediaPresignFiles(
          signedUploads,
          lineItemId,
        );
        const response = await getS3SignedUrls(
          filesMapping,
          returnItems.order_id,
          returnItems.order_number,
          lineItemId,
        );
        const posts = response.data.posts || [];
        const postMap = posts.reduce((acc, post) => {
          acc[post.name] = post;
          return acc;
        }, {});

        await Promise.all(
          signedUploads.map(({ file, requestName }) => {
            const post = postMap[requestName];
            if (!post?.post_data || !post.upload_media_id) {
              throw new Error(`No presigned upload was created for ${file.name}`);
            }
            return uploadMediaToS3(file, post.post_data);
          }),
        );

        uploadedMediaIds = signedUploads.map(
          ({ requestName }) => postMap[requestName].upload_media_id,
        );
        setImageQCUploadedMediaIdsByItem((prev) => ({
          ...prev,
          [itemKey]: uploadedMediaIds,
        }));
      }

      const payload = buildReturnImageQCResubmissionPayload({
        returnRequestId: returnItems.id,
        lineItemId,
        uploadedMediaIds,
      });
      const response = await resubmitReturnImageQC(payload);
      const attempt = response.data.attempt;

      (imageQCPreviewsByItem[itemKey] || []).forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      setImageQCFilesByItem((prev) => ({ ...prev, [itemKey]: [] }));
      setImageQCPreviewsByItem((prev) => ({ ...prev, [itemKey]: [] }));
      setImageQCUploadedMediaIdsByItem((prev) =>
        clearReturnImageQCUploadedMediaIds(prev, itemKey),
      );
      setImageQCSuccessByItem((prev) => ({
        ...prev,
        [itemKey]: `Your images were submitted for verification (attempt ${attempt}).`,
      }));
      setReturnItems((prevItems) => ({
        ...prevItems,
        alerts: Array.isArray(prevItems.alerts)
          ? prevItems.alerts.filter(
              (alert) => !isReturnImageQCAlertForLineItem(alert, item),
            )
          : prevItems.alerts,
        line_items: Array.isArray(prevItems.line_items)
          ? markReturnImageQCLineItemPending(
              prevItems.line_items,
              imageQCAlert,
              attempt,
            )
          : prevItems.line_items,
      }));
    } catch (error) {
      setImageQCUploadedMediaIdsByItem((prev) =>
        clearReturnImageQCUploadedMediaIds(prev, itemKey),
      );
      setImageQCErrorByItem((prev) => ({
        ...prev,
        [itemKey]: getApiErrorMessage(
          error,
          "Unable to submit the additional images. Please try again.",
        ),
      }));
    } finally {
      setImageQCUploadingByItem((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  const formatStatusLabel = (value) => {
    if (value === undefined || value === null || value === "") {
      return "";
    }

    return String(value)
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const getShipmentStatusTone = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();

    if (
      normalizedStatus.includes("deliver") ||
      normalizedStatus.includes("complete")
    ) {
      return "success";
    }

    if (
      normalizedStatus.includes("reject") ||
      normalizedStatus.includes("cancel") ||
      normalizedStatus.includes("fail")
    ) {
      return "danger";
    }

    return "warning";
  };

  const trackingTitle = returnItems?.status
    ? formatStatusLabel(returnItems.status)
    : "Return Requested";

  const getShipmentMethod = () =>
    returnItems?.shipment_handled_by ||
    returnItems?.customer_preferred_return_shipment_method ||
    "";

  const lineItems = Array.isArray(returnItems.line_items)
    ? returnItems.line_items
    : [];

  const isSelfShipValue = (value) => {
    const normalizedValue = String(value || "").toLowerCase();
    return (
      normalizedValue === "customer" ||
      normalizedValue.includes("customer_handles") ||
      normalizedValue.includes("customer_ships") ||
      normalizedValue.includes("self_ship")
    );
  };

  const hasSelfShipLineItem = lineItems.some((item) =>
    isSelfShipValue(
      item.return_shipment_method ||
        item.customer_preferred_return_shipment_method ||
        item.shipment_handled_by,
    ),
  );

  const isCustomerShipment = () => {
    return isSelfShipValue(getShipmentMethod()) || hasSelfShipLineItem;
  };

  const selfShipAddresses = Array.isArray(
    returnItems.self_ship_addresses_to_show,
  )
    ? returnItems.self_ship_addresses_to_show
    : Array.isArray(returnItems.self_addresses_to_show)
      ? returnItems.self_addresses_to_show
      : [];
  const hasSelfShipAddresses = selfShipAddresses.length > 0;
  const trackingAlerts = Array.isArray(returnItems.alerts)
    ? returnItems.alerts.filter(
        (alert) =>
          (alert?.is_active ?? alert?.status === "active") &&
          (!alert?.audience || alert.audience === "customer") &&
          !(
            (trackingDetailsSubmitted || !hasSelfShipAddresses) &&
            alert?.cta?.target === "self_ship_details"
          ),
      )
    : [];
  const selfShipWarehouseRef = trackingAlerts.find(
    (alert) => alert?.cta?.target === "self_ship_details",
  )?.context?.warehouse_ref;
  const requestWarehouseAddress =
    selfShipAddresses.find(
      (address) => String(address?.id) === String(selfShipWarehouseRef),
    ) || selfShipAddresses[0];
  const formatAddress = (address = {}) =>
    [
      address.name,
      address.address_line_1,
      address.address_line_2,
      address.city,
      address.state,
      address.country,
      address.pin,
    ]
      .filter(Boolean)
      .join(", ");
  const warehouseAddress =
    formatAddress(requestWarehouseAddress) ||
    formatAddress(rmsConfig.return_address);

  const copyWarehouseAddress = () => {
    if (navigator?.clipboard && warehouseAddress) {
      navigator.clipboard.writeText(warehouseAddress).then(() => {
        setAddressCopied(true);
        window.setTimeout(() => setAddressCopied(false), 2000);
      });
    }
  };

  const scrollToTrackingDetails = () => {
    if (!shippingDetailsRef.current) {
      return;
    }

    const headerOffset = 84;
    const targetPosition =
      shippingDetailsRef.current.getBoundingClientRect().top +
      window.pageYOffset -
      headerOffset;

    window.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: "smooth",
    });
  };

  const scrollToPaymentDetails = (paymentDetailId) => {
    const paymentTarget =
      paymentDetailRefs.current[String(paymentDetailId)] ||
      paymentDetailsRef.current;

    if (!paymentTarget) {
      return;
    }

    const headerOffset = 84;
    const targetPosition =
      paymentTarget.getBoundingClientRect().top +
      window.pageYOffset -
      headerOffset;

    window.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: "smooth",
    });
  };

  const getAlertSeverityClass = (severity = "") => {
    const normalizedSeverity = String(severity).toLowerCase();

    if (normalizedSeverity === "error") {
      return "danger";
    }

    if (normalizedSeverity === "warning") {
      return "warning";
    }

    return "info";
  };

  const getAlertIcon = (severity = "") => {
    const alertClass = getAlertSeverityClass(severity);

    if (alertClass === "danger") {
      return <ErrorOutlineIcon />;
    }

    if (alertClass === "warning") {
      return <WarningAmberOutlinedIcon />;
    }

    return <InfoOutlinedIcon />;
  };

  const handleAlertCta = (alert = {}) => {
    const cta = alert.cta || {};

    if (isReturnImageQCUploadAlert(alert)) {
      const itemIndex = lineItems.findIndex((item) =>
        isReturnImageQCAlertForLineItem(alert, item),
      );
      if (itemIndex >= 0) {
        const itemKey = getPickupProofItemKey(lineItems[itemIndex], itemIndex);
        imageQCUploadRefs.current[itemKey]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    if (cta.target === "self_ship_details") {
      scrollToTrackingDetails();
      return;
    }

    const target = String(cta.target || "").toLowerCase();
    const action = String(alert.action || cta.action || "").toLowerCase();
    const label = String(cta.label || "").toLowerCase();
    const paymentDetailId =
      cta.resource_id || alert?.context?.payment_detail_id || alert.source_id;

    if (
      target.includes("payment") ||
      action.includes("payment") ||
      action.includes("pay") ||
      label.includes("pay")
    ) {
      const paymentDetail =
        exchangePaymentDetails.find(
          (details) => String(details.id) === String(paymentDetailId),
        ) ||
        exchangePaymentDetails.find((details) => {
          const status = String(
            details.payment_status || details.tx_status || "",
          ).toLowerCase();
          return status === "pending" || status === "initiated";
        });

      if (paymentDetail?.payment_url) {
        window.location.assign(paymentDetail.payment_url);
        return;
      }

      scrollToPaymentDetails(paymentDetailId);
    }
  };

  const renderTrackingAlerts = () => {
    if (trackingAlerts.length === 0) {
      return null;
    }

    return trackingAlerts.map((alert) => {
      const alertClass = getAlertSeverityClass(alert.severity);
      const ctaLabel =
        alert.cta?.label ||
        (isReturnImageQCUploadAlert(alert) ? "Upload images" : "");

      return (
        <section
          className={`tracking-card tracking-alert-banner ${alertClass}`}
          key={alert.id || alert.trace_id || alert.title}
        >
          <div className="tracking-alert-title">
            {getAlertIcon(alert.severity)}
            <p>{alert.title}</p>
          </div>
          {alert.message ? <span>{alert.message}</span> : null}
          {ctaLabel ? (
            <button
              type="button"
              className="tracking-alert-link"
              onClick={() => handleAlertCta(alert)}
            >
              {ctaLabel}
            </button>
          ) : null}
        </section>
      );
    });
  };

  const getLineItemRequestTypeLabel = (product = {}) =>
    lineItemRequestTypeLabel(product, returnItems.return_type);

  const getWarrantyStatus = (product = {}) =>
    returnItems.warranty_status_label ||
    product.warranty_status_label ||
    returnItems.warranty_status_detail?.label ||
    product.warranty_status_detail?.label ||
    "";

  const isExchangeRequest = hasExchangeFulfillmentRequestType(
    returnItems.return_type,
  );
  const exchangePaymentDetails = customerPayments(returnItems, isExchangeRequest);
  const hasExchangeDetails = returnItems.exchange_details?.length > 0;
  const hasRefundDetails = returnItems.refund_details?.length > 0;
  const formatPreferenceLabel = (value) => {
    if (!value) {
      return "-";
    }

    return String(value)
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const formatShippingPreferenceLabel = (value) => {
    if (!value) {
      return "-";
    }

    const normalizedValue = String(value).toLowerCase();

    if (
      normalizedValue === "customer" ||
      normalizedValue.includes("customer_handles") ||
      normalizedValue.includes("customer_ships") ||
      normalizedValue.includes("self_ship")
    ) {
      return "Customer Self-Ship";
    }

    if (normalizedValue.includes("merchant_handles")) {
      return "Merchant Pickup";
    }

    return formatPreferenceLabel(value);
  };

  const normalizeShippingPreference = (value) => {
    const normalizedValue = String(value || "").toLowerCase();

    if (
      normalizedValue === "customer" ||
      normalizedValue.includes("customer_handles") ||
      normalizedValue.includes("customer_ships") ||
      normalizedValue.includes("self_ship")
    ) {
      return "customer_self_ship";
    }

    if (
      normalizedValue.includes("merchant_handles") ||
      normalizedValue === "merchant" ||
      normalizedValue.includes("merchant pickup") ||
      normalizedValue.includes("merchant handles")
    ) {
      return "merchant_pickup";
    }

    return normalizedValue.trim();
  };

  const originalShippingPreference =
    returnItems.original_customer_preferred_return_shipment_method ||
    returnItems.original_return_shipment_method ||
    returnItems.customer_preferred_return_shipment_method;
  const currentReturnShippingMethod =
    returnItems.current_return_shipping_method ||
    returnItems.current_return_shipment_method ||
    returnItems.return_shipment_method ||
    returnItems.shipment_handled_by ||
    returnItems.customer_preferred_return_shipment_method;
  const displayedShippingPreference =
    currentReturnShippingMethod || originalShippingPreference;
  const normalizedOriginalShippingPreference = normalizeShippingPreference(
    originalShippingPreference,
  );
  const normalizedCurrentShippingMethod = normalizeShippingPreference(
    currentReturnShippingMethod,
  );
  const hasShippingPreferenceChange =
    Boolean(normalizedOriginalShippingPreference) &&
    Boolean(normalizedCurrentShippingMethod) &&
    normalizedOriginalShippingPreference !== normalizedCurrentShippingMethod;
  const shippingPreferenceChangeDisplayReason =
    "Merchant pickup could not be arranged for this request";
  const isCustomerAwb = (value) =>
    String(value || "")
      .trim()
      .toLowerCase() === "customer";
  const visibleShipmentLineItems = Array.isArray(
    returnItems.shipment_line_items,
  )
    ? returnItems.shipment_line_items.filter(
        (item) => !isCustomerAwb(item?.awb),
      )
    : [];
  const shouldShowReturnPreferences =
    Boolean(displayedShippingPreference) ||
    Boolean(returnItems.customer_preferred_refund_method);
  const isCustomerShipmentAllocated =
    String(returnItems?.status || "")
      .toLowerCase()
      .replaceAll("_", " ") === "customer shipment allocated";
  const trackingDescription = progressedShippingDescription(returnItems) || (isCustomerShipmentAllocated
    ? "Tracking details have been submitted. The merchant will review the shipment and continue processing once the item is received."
    : hasShippingPreferenceChange
    ? "Merchant pickup could not be arranged for this request. Please ship the item yourself and submit tracking details."
    : isCustomerShipment()
      ? "Please ship your item to complete the return."
      : "Please keep the item packed with original tags and invoice for pickup.");

  const formatVariantSummary = (item) => {
    if (!item?.variant_name || item.variant_name.indexOf("Default") !== -1) {
      return "";
    }

    return item.variant_name
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" • ");
  };

  const renderLineItemPrice = (item) => {
    if (!item) {
      return null;
    }

    if (item.total_item_discount > 0) {
      return (
        <span className="line-item-price-stack">
          <span className="line-item-current-price">
            ₹ {item.discounted_price}
          </span>
          <span className="line-item-compare-price">
            ₹{item.original_product_price}
          </span>
        </span>
      );
    }

    if (item.product_price === undefined || item.product_price === null) {
      return null;
    }

    return `₹ ${item.product_price}`;
  };

  const formatMoney = (amount) => {
    if (amount === undefined || amount === null || amount === "") {
      return null;
    }

    return `₹ ${amount}`;
  };

  const getLineItemReason = (item) => {
    if (!item?.customer_reason_label) {
      return "";
    }

    return `${item.customer_reason_label}${
      item.customer_sub_reason_label
        ? ` / ${item.customer_sub_reason_label}`
        : ""
    }`;
  };

  const getLineItemReplacement = (item) => {
    const returnType = String(item?.return_type || "").toLowerCase();

    if (
      !returnType.includes("exchange") &&
      !returnType.includes("replace")
    ) {
      return null;
    }

    return {
      imageSrc:
        typeof item.exchange_variant_image_url === "string"
          ? item.exchange_variant_image_url
          : "",
      title: item.exchange_product_title,
      variant: formatVariantSummary({
        variant_name: item.exchange_variant_name,
      }),
      quantity: item.exchange_quantity || item.quantity,
      price: formatMoney(item.exchange_product_price),
    };
  };

  const getPaymentAmount = (details) => {
    const amount =
      details?.amount ??
      details?.due_amount ??
      details?.total_exchange_charges ??
      details?.total_refund_charges ??
      details?.total_warranty_charges;

    if (amount === undefined || amount === null || amount === "") {
      return "-";
    }

    return `₹ ${amount}`;
  };

  const getPaymentStatus = (details) =>
    String(details?.payment_status || details?.tx_status || "").toLowerCase();
  const isPendingPayment = (details) => {
    const paymentStatusLabel = getPaymentStatus(details);

    return (
      paymentStatusLabel === "pending" || paymentStatusLabel === "initiated"
    );
  };

  const isExchangeLineItem = (item = {}) => {
    const returnType = String(item.return_type || "").toLowerCase();
    return (
      returnType.includes("exchange") || returnType.includes("replace")
    );
  };

  const getLineItemTimeline = (item = {}) =>
    isWarrantyTimeline(item) ? warrantyTimelineSteps(item) : Array.isArray(item.timeline) ? item.timeline : [];

  const isAwaitingTrackingDetailsLabel = (label) => {
    const normalizedLabel = String(label || "")
      .toLowerCase()
      .replaceAll("_", " ");

    return (
      normalizedLabel.includes("awaiting tracking details") ||
      normalizedLabel.includes("tracking details pending")
    );
  };

  const isAwaitingTrackingDetails = (item = {}) => {
    const timeline = getLineItemTimeline(item);
    const currentTimeline =
      timeline.find((entry) => entry.current) ||
      [...timeline].reverse().find((entry) => entry.completed);

    return isAwaitingTrackingDetailsLabel(
      currentTimeline?.label || item?.status || item?.return_shipment_status,
    );
  };

  const markTrackingDetailsSubmitted = (item = {}) => {
    const timeline = getLineItemTimeline(item);

    if (timeline.length === 0) {
      return {
        ...item,
        status: "Details submitted successfully",
        return_shipment_awb: awbNumber,
        return_shipment_company: courierName,
        return_shipment_url: trackingLink,
        return_shipment_status: "details_submitted",
      };
    }

    return {
      ...item,
      status: "Details submitted successfully",
      return_shipment_awb: awbNumber,
      return_shipment_company: courierName,
      return_shipment_url: trackingLink,
      return_shipment_status: "details_submitted",
      timeline: timeline.map((entry) =>
        entry.current || isAwaitingTrackingDetailsLabel(entry.label)
          ? {
              ...entry,
              label: "Details submitted successfully",
              current: true,
              completed: false,
              timestamp: new Date().toISOString(),
            }
          : entry,
      ),
    };
  };

  const getRejectedTimelineEntry = (item = {}) =>
    getLineItemTimeline(item).find(
      (entry) =>
        entry.code === "T2" &&
        (entry.current || entry.completed || entry.label === "Rejected"),
    );

  const isLineItemRejected = (item = {}) =>
    Boolean(item.rejected) || Boolean(getRejectedTimelineEntry(item));

  const getLineItemRejectedReason = (item = {}) =>
    item.rejected_reason ||
    item.reject_reason ||
    item.pickup_qc_failure_reason ||
    item.rejected_reason_label ||
    item.rejection_reason ||
    "";

  const getLineItemCurrentTimeline = (item = {}) => {
    if (isLineItemRejected(item)) {
      return (
        getRejectedTimelineEntry(item) || { code: "T2", label: "Rejected" }
      );
    }

    const timeline = getLineItemTimeline(item);
    return (
      timeline.find((entry) => entry.current) ||
      [...timeline].reverse().find((entry) => entry.completed) ||
      timeline[0] || { code: "T0", label: "Pending" }
    );
  };

  const getTimelineSteps = (item = {}) => {
    if (isWarrantyTimeline(item)) return warrantyTimelineSteps(item);
    const timelineByCode = getLineItemTimeline(item).reduce((acc, entry) => {
      if (entry.code) {
        acc[entry.code] = entry;
      }
      return acc;
    }, {});
    const terminalCode = isExchangeLineItem(item) ? "T10" : "T9";
    const currentCode = getLineItemCurrentTimeline(item)?.code || "T0";
    const applicableSteps = APPLICABLE_TIMELINES.filter((step) => {
      if (step.code === "T2") {
        return false;
      }
      if (step.code === "T9" || step.code === "T10") {
        return step.code === terminalCode;
      }
      return true;
    });
    const currentIndex = applicableSteps.findIndex(
      (step) => step.code === currentCode,
    );

    return applicableSteps.map((step, index) => {
      const backendStep = timelineByCode[step.code] || {};
      const isTerminalStep =
        step.code === terminalCode &&
        (Boolean(backendStep.current) ||
          Boolean(backendStep.completed) ||
          step.code === currentCode);

      return {
        ...step,
        ...backendStep,
        completed:
          Boolean(backendStep.completed) ||
          (currentIndex !== -1 && index < currentIndex),
        current: Boolean(backendStep.current) || step.code === currentCode,
        finalCompleted: isTerminalStep,
        label: backendStep.label || step.label,
        timestamp:
          backendStep.timestamp ||
          (step.code === "T0"
            ? item.created_at || returnItems.created_at
            : undefined),
      };
    });
  };

  const getInlineTimelineSteps = (item = {}) => {
    if (isWarrantyTimeline(item)) {
      return warrantyTimelineSteps(item).map((step) => ({
        ...step, codes: [step.code], terminal: step.code === "W6" || step.code === "T2",
      }));
    }
    const currentCode = getLineItemCurrentTimeline(item)?.code || "T0";
    const terminalCode = isExchangeLineItem(item) ? "T10" : "T9";
    const detailedSteps = getTimelineSteps(item);
    const groups = [
      ["T0", "T1"],
      ["T3"],
      ["T4", "T5"],
      ["T6", "T7", "T8"],
      [terminalCode],
    ];
    const currentGroupIndex = groups.findIndex((codes) =>
      codes.includes(currentCode),
    );

    return groups.map((codes, index) => ({
      codes,
      current: codes.includes(currentCode),
      terminal: codes.includes(terminalCode),
      completed:
        index < currentGroupIndex ||
        codes.some((code) =>
          detailedSteps.some((step) => step.code === code && step.completed),
        ),
      finalCompleted:
        codes.includes(terminalCode) &&
        codes.some((code) =>
          detailedSteps.some(
            (step) => step.code === code && (step.current || step.completed),
          ),
        ),
    }));
  };

  const formatTimelineTimestamp = (timestamp) => {
    if (!timestamp) {
      return "";
    }

    return format(new Date(timestamp), "dd MMM yyyy, hh:mm aa");
  };

  const renderAdditionalQCImagesSection = (product, itemKey, alert) => {
    const successMessage = imageQCSuccessByItem[itemKey];
    if (!alert && !successMessage) {
      return null;
    }

    if (successMessage) {
      return (
        <section
          className="return-qc-upload-section return-qc-upload-success"
          ref={(node) => {
            imageQCUploadRefs.current[itemKey] = node;
          }}
        >
          <CheckCircleOutlinedIcon />
          <div>
            <p>Images submitted</p>
            <span>{successMessage}</span>
          </div>
        </section>
      );
    }

    const previews = imageQCPreviewsByItem[itemKey] || [];
    const failureMessages = getReturnImageQCFailureMessages(alert);
    const missingChecks = getReturnImageQCMissingChecks(alert);
    const hasPreviews = previews.length > 0;
    const isUploading = Boolean(imageQCUploadingByItem[itemKey]);
    const hasUploadedMedia =
      (imageQCUploadedMediaIdsByItem[itemKey] || []).length > 0;
    const inputId = `return-qc-images-${itemKey}`;

    return (
      <section
        className="return-qc-upload-section"
        ref={(node) => {
          imageQCUploadRefs.current[itemKey] = node;
        }}
      >
        <div className="return-qc-upload-header">
          <div>
            <p>Additional verification images</p>
            <span>
              Add clear images of this item to complete return verification.
            </span>
          </div>
        </div>

        {missingChecks.length > 0 ? (
          <div className="return-qc-missing-checks">
            {missingChecks.map((check) => (
              <span key={check}>{formatStatusLabel(check)}</span>
            ))}
          </div>
        ) : null}

        {failureMessages.length > 0 ? (
          <ul className="return-qc-guidance-list">
            {failureMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : null}

        <input
          id={inputId}
          type="file"
          multiple
          accept="image/*"
          className="media-upload-input"
          onChange={(event) => handleImageQCFiles(itemKey, event)}
          disabled={isUploading || hasUploadedMedia}
        />

        {!hasPreviews ? (
          <label htmlFor={inputId}>
            <div className="media-upload-dropzone">
              <div className="media-upload-dropzone-icon">
                <AddPhotoAlternateOutlinedIcon />
              </div>
              <div>
                <p className="media-upload-title">Add images</p>
                <p className="media-upload-description">
                  Up to {RETURN_IMAGE_QC_MAX_IMAGES} images for this item
                </p>
              </div>
            </div>
          </label>
        ) : (
          <div className="media-upload-preview-list return-qc-preview-list">
            {previews.map((file, index) => (
              <div className="media-preview-tile" key={`${file.name}-${index}`}>
                <img
                  src={file.preview}
                  alt={file.name}
                  className="media-preview-asset"
                />
                {!isUploading && !hasUploadedMedia ? (
                  <button
                    type="button"
                    className="media-preview-remove"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeImageQCFile(itemKey, index)}
                  >
                    <CloseIcon />
                  </button>
                ) : null}
              </div>
            ))}
            {!isUploading && !hasUploadedMedia ? (
              <label htmlFor={inputId} className="return-qc-add-tile">
                <AddPhotoAlternateOutlinedIcon />
                <span>Add more</span>
              </label>
            ) : null}
          </div>
        )}

        <div className="media-upload-counts">
          <p className="media-upload-count">
            <PhotoCameraOutlinedIcon />
            {previews.length} image{previews.length === 1 ? "" : "s"} selected
            {hasPreviews ? (
              <CheckCircleOutlinedIcon className="media-upload-success-icon" />
            ) : null}
          </p>
        </div>

        {imageQCErrorByItem[itemKey] ? (
          <div className="tracking-details-message error">
            {imageQCErrorByItem[itemKey]}
          </div>
        ) : null}

        <Button
          type="button"
          variant="contained"
          className="tracking-submit-button return-qc-submit"
          disabled={!hasPreviews || isUploading}
          onClick={() => submitAdditionalQCImages(product, itemKey)}
        >
          {isUploading ? (
            <>
              <CircularProgress size={16} color="inherit" />
              Submitting...
            </>
          ) : hasUploadedMedia ? (
            "Retry verification"
          ) : (
            "Submit images"
          )}
        </Button>
      </section>
    );
  };

  const renderPickupProofSection = (product, itemKey, itemIndex) => {
    if (
      !product?.pickup_image_submission &&
      !pickupProofSuccessByItem[itemKey]
    ) {
      return null;
    }

    if (pickupProofSuccessByItem[itemKey]) {
      return (
        <section className="pickup-proof-section pickup-proof-success">
          <CheckCircleOutlinedIcon />
          <div>
            <p>Pickup proof uploaded</p>
            <span>{pickupProofSuccessByItem[itemKey]}</span>
          </div>
        </section>
      );
    }

    const previews = pickupProofPreviewsByItem[itemKey] || [];
    const hasPreviews = previews.length > 0;
    const inputId = `pickup-proof-${itemKey}`;
    const isUploading = Boolean(pickupProofUploadingByItem[itemKey]);

    return (
      <section className="pickup-proof-section">
        <div className="pickup-proof-header">
          <div>
            <p>Pickup proof</p>
            <span>
              Upload clear images or videos of this item before pickup.
            </span>
          </div>
        </div>

        <input
          id={inputId}
          type="file"
          multiple
          accept="image/*,video/*"
          className="media-upload-input"
          onChange={(event) => handlePickupProofFiles(itemKey, event)}
          disabled={isUploading}
        />

        {!hasPreviews ? (
          <label htmlFor={inputId}>
            <div className="media-upload-dropzone">
              <div className="media-upload-dropzone-icon">
                <AddPhotoAlternateOutlinedIcon />
              </div>
              <div>
                <p className="media-upload-title">Add pickup proof</p>
                <p className="media-upload-description">
                  Add images or videos for this item
                </p>
              </div>
            </div>
          </label>
        ) : (
          <div className="media-upload-preview-list pickup-proof-preview-list">
            {previews.map((file, index) => (
              <div className="media-preview-tile" key={`${file.name}-${index}`}>
                {file.type?.startsWith("video/") ? (
                  <video
                    src={file.preview}
                    className="media-preview-asset"
                    controls
                    muted
                    preload="metadata"
                  />
                ) : file.type === "application/pdf" ? (
                  <div className="media-preview-file">
                    <InsertDriveFileOutlinedIcon />
                  </div>
                ) : (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="media-preview-asset"
                  />
                )}
                {!isUploading ? (
                  <button
                    type="button"
                    className="media-preview-remove"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removePickupProofFile(itemKey, index)}
                  >
                    <CloseIcon />
                  </button>
                ) : null}
              </div>
            ))}
            {!isUploading ? (
              <label htmlFor={inputId} className="pickup-proof-add-tile">
                <AddPhotoAlternateOutlinedIcon />
                <span>Add more</span>
              </label>
            ) : null}
          </div>
        )}

        <div className="media-upload-counts">
          <p className="media-upload-count">
            <PhotoCameraOutlinedIcon />
            {previews.length} pickup proof file{previews.length === 1 ? "" : "s"}{" "}
            selected
            {hasPreviews ? (
              <CheckCircleOutlinedIcon className="media-upload-success-icon" />
            ) : null}
          </p>
        </div>

        {pickupProofErrorByItem[itemKey] ? (
          <div className="tracking-details-message error">
            {pickupProofErrorByItem[itemKey]}
          </div>
        ) : null}

        <Button
          type="button"
          variant="contained"
          className="tracking-submit-button pickup-proof-submit"
          disabled={!hasPreviews || isUploading}
          onClick={() => submitPickupProof(product, itemKey, itemIndex)}
        >
          {isUploading ? (
            <>
              <CircularProgress size={16} color="inherit" />
              Uploading...
            </>
          ) : (
            "Update pickup proof"
          )}
        </Button>
      </section>
    );
  };

  const renderTrackingLineItems = () => {
    if (!lineItems || lineItems.length === 0) {
      return null;
    }

    return (
      <div className="tracking-line-items-section">
        <div className="tracking-line-items-list">
          {lineItems.map((product, index) => {
            const currentTimeline = getLineItemCurrentTimeline(product);
            const rejected = isLineItemRejected(product);
            const rejectedReason = getLineItemRejectedReason(product);
            const requestTypeLabel = getLineItemRequestTypeLabel(product);
            const refundMethod = lineItemRefundMethod(product);
            const warrantyStatus = getWarrantyStatus(product);
            const pickupProofItemKey = getPickupProofItemKey(product, index);
            const imageQCAlert = findReturnImageQCUploadAlert(
              trackingAlerts,
              product,
            );

            return (
              <div
                className="tracking-line-items-card tracking-line-item-box"
                key={`line-item-${product.line_item_id || index}`}
              >
                {requestTypeLabel ? (
                  <span className="tracking-request-type-tag">
                    {requestTypeLabel}
                  </span>
                ) : null}
                <LineItemCard
                  product={product}
                  imageSrc={product.variant_image_url}
                  title={product.product_title}
                  variant={formatVariantSummary(product)}
                  quantity={product.quantity}
                  price={renderLineItemPrice(product)}
                  replacement={getLineItemReplacement(product)}
                  primaryColor={merchantConfig.primary_theme_color}
                  rounded={merchantConfig.use_rounded_edge_buttons}
                  clickable={false}
                >
                  {getLineItemReason(product) ? (
                    <p className="tracking-line-item-reason">
                      Reason: {getLineItemReason(product)}
                    </p>
                  ) : null}
                  {refundMethod ? (
                    <p className="tracking-line-item-reason">
                      Refund Method: {formatPreferenceLabel(refundMethod)}
                    </p>
                  ) : null}
                  <CustomerInspection inspection={product.inspection} />
                  {!rejected ? (
                    <div className="tracking-inline-timeline">
                      <div className="tracking-inline-timeline-dots">
                        {getInlineTimelineSteps(product).map((step) => (
                          <div
                            key={step.codes.join("-")}
                            className={`tracking-inline-timeline-step ${
                              step.current ? "current" : ""
                            } ${step.completed ? "completed" : ""} ${
                              step.finalCompleted ? "final-completed" : ""
                            }`}
                          >
                            <span className="tracking-inline-timeline-dot" />
                          </div>
                        ))}
                      </div>
                      <strong className="tracking-inline-current-label">
                        {currentTimeline?.label || "Pending"}
                      </strong>
                      <div
                        className={`tracking-timeline-actions ${
                          warrantyStatus ? "has-status" : ""
                        }`}
                      >
                        {warrantyStatus ? (
                          <span className="tracking-warranty-status">
                            Resolution: {warrantyStatus}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="tracking-view-timeline"
                          onClick={() => setTimelineItem(product)}
                        >
                          See all updates
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="tracking-line-item-status rejected">
                      <div>
                        <strong>{currentTimeline?.label || "Rejected"}</strong>
                      </div>
                    </div>
                  )}
                  {rejected && rejectedReason ? (
                    <p className="tracking-line-item-rejected-reason">
                      Rejected Reason: {rejectedReason}
                    </p>
                  ) : null}
                  {renderAdditionalQCImagesSection(
                    product,
                    pickupProofItemKey,
                    imageQCAlert,
                  )}
                  {renderPickupProofSection(
                    product,
                    pickupProofItemKey,
                    index,
                  )}
                </LineItemCard>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderShippingDetailsSection = () => {
    if (!isCustomerShipment() || !hasSelfShipAddresses) {
      return null;
    }

    if (trackingDetailsSubmitted) {
      return trackingDetailsSuccessMessage ? (
        <section className="tracking-card tracking-details-submitted">
          <CheckCircleOutlinedIcon />
          <div>
            <p>Tracking details submitted</p>
            <span>{trackingDetailsSuccessMessage}</span>
          </div>
        </section>
      ) : null;
    }

    const hasReceipt = Boolean(preview);

    return (
      <section
        className="tracking-card shipping-details-card"
        ref={shippingDetailsRef}
      >
        <div className="tracking-card-header">
          <p className="tracking-section-title">Add tracking details</p>
          <div className="tracking-details-header-actions">
            <button
              type="button"
              className="tracking-how-to-ship-button"
              onClick={() => setShowShipGuide(true)}
            >
              <HelpOutlineIcon />
              How to Ship
            </button>
            {disabled ? <span>Submitted</span> : null}
          </div>
        </div>

        {warehouseAddress ? (
          <div className="shipping-details-address">
            <div className="shipping-details-address-header">
              <p className="tracking-detail-label">
                Ship the item to this address
              </p>
              <button
                type="button"
                className={`tracking-copy-address ${
                  addressCopied ? "copied" : ""
                }`}
                onClick={copyWarehouseAddress}
              >
                {addressCopied ? (
                  <>
                    Copied
                    <CheckCircleIcon className="tracking-copy-success-icon" />
                  </>
                ) : (
                  <>
                    Copy Address
                    <ContentCopyOutlinedIcon />
                  </>
                )}
              </button>
            </div>
            <div className="shipping-details-address-value">
              <LocationOnOutlinedIcon />
              <p>{warehouseAddress}</p>
            </div>
          </div>
        ) : null}

        <form onSubmit={(e) => submitHandler(e)}>
          <label>
            <span>Courier Partner Name *</span>
            <input
              type="text"
              value={courierName || ""}
              disabled={disabled}
              onChange={(e) => setCourierName(e.target.value)}
              required
              placeholder="Courier partner name"
            />
          </label>

          <label>
            <span>AWB / Tracking ID *</span>
            <input
              type="text"
              value={awbNumber || ""}
              disabled={disabled}
              onChange={(e) => setAwbNumber(e.target.value)}
              required
              placeholder="AWB / Tracking ID"
            />
          </label>

          <label>
            <span>Tracking Link *</span>
            <input
              type="text"
              value={trackingLink || ""}
              disabled={disabled}
              onChange={(e) => setTrackingLink(e.target.value)}
              required
              placeholder="Tracking Link"
            />
          </label>

          <div className="receipt-upload media-upload">
            <p>Receipt Image *</p>
            {hasReceipt ? (
              <div className="media-upload-preview-list receipt-preview-list">
                <div className="media-preview-tile">
                  {uploads?.type?.startsWith("video/") ? (
                    <video
                      src={preview}
                      className="media-preview-asset"
                      controls
                      muted
                      preload="metadata"
                    />
                  ) : uploads?.type === "application/pdf" ? (
                    <div className="media-preview-file">
                      <InsertDriveFileOutlinedIcon />
                    </div>
                  ) : (
                    <img
                      src={preview}
                      alt="receipt preview"
                      className="media-preview-asset"
                    />
                  )}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => {
                        setUploads(null);
                        setPreview(null);
                      }}
                      className="media-preview-remove"
                    >
                      <CloseIcon />
                    </button>
                  )}
                </div>
                {uploads?.name ? (
                  <div className="receipt-file-details">
                    <p>{uploads.name}</p>
                    {!disabled ? (
                      <span>Remove this file to upload another receipt</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <input
                  required
                  type="file"
                  id="self-ship-file"
                  className="media-upload-input"
                  accept="image/png, image/jpg, image/jpeg, application/pdf"
                  onChange={(e) => handleUploads(e)}
                />
                <label htmlFor="self-ship-file">
                  <div className="media-upload-dropzone">
                    <div className="media-upload-dropzone-icon">
                      <AddPhotoAlternateOutlinedIcon />
                    </div>
                    <div>
                      <p className="media-upload-title">
                        Upload courier receipt or shipment proof
                      </p>
                      <p className="media-upload-description">
                        Accepted formats: JPG, PNG, PDF
                      </p>
                    </div>
                  </div>
                </label>
              </>
            )}
            <div className="media-upload-counts">
              <p className="media-upload-count">
                <PhotoCameraOutlinedIcon />
                {hasReceipt ? "1/1 receipt uploaded" : "0/1 receipt uploaded"}
                {hasReceipt && (
                  <CheckCircleOutlinedIcon className="media-upload-success-icon" />
                )}
              </p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={disabled || submittingTrackingDetails}
            variant="contained"
            className="tracking-submit-button"
          >
            {submittingTrackingDetails ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Submitting...
              </>
            ) : (
              "Submit Tracking ID"
            )}
          </Button>

          {trackingDetailsErrorMessage ? (
            <div className="tracking-details-message error">
              {trackingDetailsErrorMessage}
            </div>
          ) : null}

          <div className="tracking-details-info">
            <p>What happens next?</p>
            <span>
              After you submit tracking details, the merchant will review the
              shipment and continue processing your request once the item is
              received.
            </span>
          </div>

          <div className="tracking-details-info">
            <p>Courier charges</p>
            <span>
              If you have questions about courier charges or reimbursement,
              please contact support before shipping the item.
            </span>
          </div>
        </form>
      </section>
    );
  };

  const renderSelfShipGuide = () => {
    if (!isCustomerShipment()) {
      return null;
    }

    return (
      <div className="self-ship-guide-content">
        <p className="tracking-section-title">How to ship your item</p>
        <div className="self-ship-steps">
          <div>
            <span className="self-ship-step-icon">
              <Inventory2OutlinedIcon />
            </span>
            Pack the item securely
          </div>
          <div>
            <span className="self-ship-step-icon">
              <LocalOfferOutlinedIcon />
            </span>
            Include original tags if possible
          </div>
          <div>
            <span className="self-ship-step-icon">
              <LocalShippingOutlinedIcon />
            </span>
            Ship via any courier service
          </div>
        </div>
      </div>
    );
  };

  return (
    <StyledApp>
      <PageHeader
        logoSrc={merchantConfig.brand_logo}
        logoAlt={merchantConfig.shop_display_name}
        primaryColor={merchantConfig.primary_theme_color}
        onBack={() => {
          const targetPath = backTo === "products" ? "/products" : "/orders";
          let url = versionedPath(targetPath, location.pathname);
          if (encipherencode) {
            url += `?encipherencode=${encipherencode}`;
          }
          navigate(url);
        }}
      />

      {apiErrorLoading ? (
        <LoadingError minHeight="80vh" />
      ) : returnItemsLoader ? (
        <V2TrackingBoneyardLoader />
      ) : (
        <StyledTracking
          primaryColor={merchantConfig.primary_theme_color}
          rounded={merchantConfig.use_rounded_edge_buttons}
        >
          <PageIntro
            title={trackingTitle}
            description={trackingDescription}
            className="tracking-page-intro"
            padding="0"
          >
            <div className="tracking-meta">
              <span>
                Order{" "}
                {returnItems?.order_name && returnItems.order_name !== ""
                  ? returnItems.order_name
                  : `#${returnItems.order_number}`}
              </span>
              <span>Request ID - #{returnItems.reference_id}</span>
            </div>
          </PageIntro>

          {renderTrackingAlerts()}

          <StyledExchange
            location={currentPath}
            primaryColor={merchantConfig.primary_theme_color}
            rounded={merchantConfig.use_rounded_edge_buttons}
            className="tracking-content"
            style={{
              textAlign: "left",
              marginBottom: "40px",
              justifyContent: "space-between",
              border: "none",
              padding: "0",
            }}
          >
            <div className="products-return">
              {visibleShipmentLineItems.length > 0 && (
                <div className="tracking-line-items-card shipment-line-items-card">
                  <p
                    className="product-details"
                    style={{
                      fontWeight: "600",
                      textAlign: "center",
                    }}
                  >
                    Shipment Details
                  </p>
                  <div className="tracking-line-items-list">
                    {visibleShipmentLineItems.map((item, i) => {
                      return (
                        <Accordion
                          key={item.awb || i}
                          className="shipment-accordion"
                          style={{
                            margin: "1rem 0px",
                            borderRadius:
                              merchantConfig.use_rounded_edge_buttons
                                ? "10px"
                                : "0px",
                            position: "relative",
                            // boxShadow: "none",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon
                                style={{
                                  color: merchantConfig.primary_theme_color,
                                  fontSize: "24px",
                                }}
                              />
                            }
                            aria-controls="panel1-content"
                            style={{
                              borderTopRightRadius:
                                merchantConfig.use_rounded_edge_buttons
                                  ? "10px"
                                  : 0,
                              borderTopLeftRadius:
                                merchantConfig.use_rounded_edge_buttons
                                  ? "10px"
                                  : 0,
                              padding: "0.5rem 1rem",
                            }}
                            sx={{
                              // border: `1px solid ${merchantConfig.primary_theme_color}`,
                              color: merchantConfig.primary_theme_color,
                              overflow: "hidden",
                              borderRadius:
                                merchantConfig.use_rounded_edge_buttons
                                  ? "10px"
                                  : "0px",
                              // when expanded, remove the overall rounding:
                              "&.Mui-expanded": {
                                borderRadius: 0,
                                margin: 0,
                              },
                              // always round the top of the summary:
                              "& .MuiAccordionSummary-root": {
                                borderTopLeftRadius:
                                  merchantConfig.use_rounded_edge_buttons
                                    ? "10px"
                                    : 0,
                                borderTopRightRadius:
                                  merchantConfig.use_rounded_edge_buttons
                                    ? "10px"
                                    : 0,
                                // when expanded, strip its bottom corners:
                                "&.Mui-expanded": {
                                  borderBottomLeftRadius: 0,
                                  borderBottomRightRadius: 0,
                                },
                              },
                              // always round the bottom of the details:
                              "& .MuiAccordionDetails-root": {
                                borderBottomLeftRadius:
                                  merchantConfig.use_rounded_edge_buttons
                                    ? "10px"
                                    : 0,
                                borderBottomRightRadius:
                                  merchantConfig.use_rounded_edge_buttons
                                    ? "10px"
                                    : 0,
                              },
                            }}
                          >
                            <p
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <a
                                href={item.tracking_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  display: "flex",
                                  color: merchantConfig.primary_theme_color,
                                }}
                              >
                                {item.awb}{" "}
                                <OpenInNewIcon style={{ marginLeft: "4px" }} />
                              </a>{" "}
                              <span style={{ margin: "0px 6px" }}>
                                ({item.line_items.length} items)
                              </span>
                            </p>
                            <p
                              style={{
                                background: merchantConfig.primary_theme_color,
                                color: merchantConfig.secondary_theme_color,
                                borderBottomLeftRadius:
                                  merchantConfig.use_rounded_edge_buttons
                                    ? "10px"
                                    : "0px",
                                borderTopRightRadius:
                                  merchantConfig.use_rounded_edge_buttons
                                    ? "10px"
                                    : "0px",
                                padding: "1px 10px",
                                position: "absolute",
                                top: "-1px",
                                right: "-1px",
                                fontSize: "12px",
                              }}
                            >
                              {item?.shipment_status?.replaceAll("_", " ")}
                            </p>
                          </AccordionSummary>
                          <AccordionDetails
                            style={{
                              // background: `${merchantConfig.primary_theme_color}33`,
                              borderBottomLeftRadius:
                                merchantConfig.use_rounded_edge_buttons
                                  ? "10px"
                                  : "0px",
                              borderBottomRightRadius:
                                merchantConfig.use_rounded_edge_buttons
                                  ? "10px"
                                  : "0px",
                              padding: "1rem",
                              overflowX: "auto",
                            }}
                          >
                            <Stepper
                              activeStep={
                                Object.keys(item.steps).findIndex(
                                  (key) => !item.steps[key],
                                ) !== -1
                                  ? Object.keys(item.steps).findIndex(
                                      (key) => !item.steps[key],
                                    ) - 1
                                  : Object.keys(item.steps).length - 1
                              }
                              connector={<ColorlibConnector />}
                              alternativeLabel={true}
                              style={{ margin: "1rem 0" }}
                              orientation="horizontal"
                            >
                              {Object.keys(item.steps).map((label, i) => (
                                <Step key={label}>
                                  <StepLabel
                                    className="step-label"
                                    style={{ textTransform: "capitalize" }}
                                  >
                                    {label.replaceAll("_", " ")}
                                  </StepLabel>
                                </Step>
                              ))}
                            </Stepper>
                            <Grid
                              alignItems="center"
                              container
                              gridTemplateColumns="repeat(12, 1fr)"
                              columnSpacing={4}
                              className="cart-products"
                            >
                              {item.line_items.map((product, i) => {
                                return (
                                  <Grid
                                    key={i}
                                    justifyContent="center"
                                    item
                                    xs={12}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                      }}
                                    >
                                      <div className="image-preview-container">
                                        <LazyLoadImage
                                          effect="opacity"
                                          className="image-preview"
                                          src={product.variant_image_url}
                                          alt={product.product_title}
                                          style={{
                                            height: "30px",
                                            width: "30px",
                                          }}
                                        />
                                      </div>
                                      <div
                                        className="details"
                                        style={{
                                          marginLeft: "0.5rem",
                                          width: "100%",
                                        }}
                                      >
                                        <p
                                          className="title"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            marginTop: "0",
                                          }}
                                        >
                                          {product.product_title}{" "}
                                          <span style={{ marginLeft: "auto" }}>
                                            Qty: {product.quantity}
                                          </span>
                                        </p>
                                        <p className="description">
                                          {product.variant_name !== null &&
                                          product.variant_name.indexOf(
                                            "Default",
                                          ) === -1
                                            ? product.variant_name
                                            : product.product_title}
                                        </p>
                                      </div>
                                    </div>
                                    {item.line_items.length - 1 !== i && (
                                      <hr style={{ margin: "1rem 0" }} />
                                    )}
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </div>
                </div>
              )}

              {renderTrackingLineItems()}

              {renderShippingDetailsSection()}

            </div>

            <div className="price-details">
              {exchangePaymentDetails && exchangePaymentDetails.length > 0 ? (
                <div className="box exchange-payment-group">
                  {(() => {
                    const firstPendingPaymentKey = exchangePaymentDetails.find(
                      (paymentDetails) => isPendingPayment(paymentDetails),
                    )?.id;
                    const hasPendingPayment =
                      firstPendingPaymentKey !== undefined &&
                      firstPendingPaymentKey !== null;

                    return (
                      <>
                        <p className="title">
                          {hasPendingPayment
                            ? "Pending payment"
                            : "Payment Details"}
                        </p>
                        {exchangePaymentDetails.map((details) => {
                          const paymentStatusLabel = getPaymentStatus(details);
                          const isPending = isPendingPayment(details);
                          return (
                            <div
                              key={details.id || details.transaction_id}
                              ref={(node) => {
                                if (details.id) {
                                  if (node) {
                                    paymentDetailRefs.current[
                                      String(details.id)
                                    ] = node;
                                  } else {
                                    delete paymentDetailRefs.current[
                                      String(details.id)
                                    ];
                                  }
                                }

                                if (
                                  isPending &&
                                  details.id === firstPendingPaymentKey
                                ) {
                                  paymentDetailsRef.current = node;
                                }
                              }}
                            >
                              <StyledRefundDetails
                                className="return-detail-card exchange-payment-detail-card"
                                roundedEdgeButtons={
                                  merchantConfig.use_rounded_edge_buttons
                                }
                                refundStatus={paymentStatusLabel}
                              >
                                <div className="refund-details-header">
                                  <div className="refund-details">
                                    <div className="refund-title">
                                      <p className="refund-amount">
                                        {getPaymentAmount(details)}
                                      </p>
                                      <p className="refund-status">
                                        {paymentStatusLabel?.replaceAll(
                                          "_",
                                          " ",
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  {details.tx_time && (
                                    <div className="refund-time">
                                      <p>
                                        {format(
                                          new Date(details.tx_time),
                                          "dd MMM yyyy",
                                        )}
                                        ,
                                      </p>
                                      <p>
                                        {format(
                                          new Date(details.tx_time),
                                          "hh:mm aa",
                                        )}
                                      </p>
                                    </div>
                                  )}
                                </div>
                                <div className="payment-reference-grid">
                                  <div>
                                    <p className="tracking-detail-label">
                                      Transaction ID
                                    </p>
                                    <p className="tracking-detail-value">
                                      {details.transaction_id || "-"}
                                    </p>
                                  </div>
                                  {details.payment_id && (
                                    <div>
                                      <p className="tracking-detail-label">
                                        Payment ID
                                      </p>
                                      <p className="tracking-detail-value">
                                        {details.payment_id}
                                      </p>
                                    </div>
                                  )}
                                </div>
                                {isPending && customerPaymentUrl(details.payment_url) && (
                                  <a
                                    href={customerPaymentUrl(details.payment_url)}
                                    style={{
                                      color:
                                        merchantConfig.secondary_theme_color,
                                      textDecoration: "none",
                                    }}
                                  >
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      sx={{
                                        backgroundColor: "#2817f0",
                                        "&:hover": {
                                          backgroundColor: "#2817f0",
                                        },
                                      }}
                                    >
                                      Pay now
                                    </Button>
                                  </a>
                                )}
                              </StyledRefundDetails>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {hasExchangeDetails && (
                  <ReturnDetails
                    detailsType="Exchange"
                    returnType={returnItems.return_type}
                    returnItems={returnItems}
                    setReturnItems={setReturnItems}
                  />
                )}

              {hasRefundDetails && (
                  <ReturnDetails
                    detailsType="Refund"
                    returnType={returnItems.return_type}
                    returnItems={returnItems}
                    setReturnItems={setReturnItems}
                  />
                )}
              {shouldShowReturnPreferences && (
                <section className="tracking-card customer-preference-card">
                  <p className="tracking-section-title">Return Preferences</p>
                  <div className="customer-preference-grid">
                    {hasShippingPreferenceChange ? (
                      <>
                        <div>
                          <p className="tracking-detail-label">
                            Original Shipping Preference
                          </p>
                          <p className="tracking-detail-value">
                            {formatShippingPreferenceLabel(
                              originalShippingPreference,
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="tracking-detail-label">
                            Current Shipping Method
                          </p>
                          <p className="tracking-detail-value">
                            {formatShippingPreferenceLabel(
                              currentReturnShippingMethod,
                            )}
                          </p>
                        </div>
                        <div className="customer-preference-wide">
                          <p className="tracking-detail-label">
                            Reason for Change
                          </p>
                          <p className="tracking-detail-value">
                            {shippingPreferenceChangeDisplayReason}
                          </p>
                        </div>
                      </>
                    ) : displayedShippingPreference ? (
                      <div>
                        <p className="tracking-detail-label">Shipping Method</p>
                        <p className="tracking-detail-value">
                          {formatShippingPreferenceLabel(
                            displayedShippingPreference,
                          )}
                        </p>
                      </div>
                    ) : null}
                    {returnItems.customer_preferred_refund_method ? (
                      <div>
                        <p className="tracking-detail-label">Refund Method</p>
                        <p className="tracking-detail-value">
                          {formatPreferenceLabel(
                            returnItems.customer_preferred_refund_method,
                          )}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </section>
              )}
              {!isCustomerShipment() ? (
                <Fragment>
                  <section className="tracking-card pickup-details-card">
                    <p className="tracking-section-title">Pickup Details</p>
                    <p className="tracking-detail-label">Address</p>
                    <PickupAddress
                      edit={false}
                      updatedAddress={updatedAddress}
                      disabled={disabled}
                      serviceable={true}
                    />
                  </section>

                  {returnItems.status < 200 && (
                    <section className="tracking-card tracking-address-card">
                      <p className="v2-resolution-note-title">Note:</p>
                      <p>
                        We will let you know the warehouse address if necessary
                        once your return request is accepted.
                      </p>
                    </section>
                  )}
                </Fragment>
              ) : null}
            </div>
          </StyledExchange>

          <BottomSheetDrawer
            open={showShipGuide}
            onClose={() => setShowShipGuide(false)}
            rounded={merchantConfig.use_rounded_edge_buttons}
          >
              <StyledTimelineDrawer
                primaryColor={merchantConfig.primary_theme_color}
                className="self-ship-guide-drawer"
              >
                <FloatingCloseButton
                  ariaLabel="Close guide"
                  className="timeline-drawer-close floating"
                  iconSize={18}
                  onClick={() => setShowShipGuide(false)}
                />
                <div className="self-ship-guide-drawer-header">
                  {renderSelfShipGuide()}
                </div>
            </StyledTimelineDrawer>
          </BottomSheetDrawer>

          <BottomSheetDrawer
            open={Boolean(timelineItem)}
            onClose={() => setTimelineItem(null)}
            rounded={merchantConfig.use_rounded_edge_buttons}
          >
            {timelineItem ? (
              <StyledTimelineDrawer
                primaryColor={merchantConfig.primary_theme_color}
              >
                <FloatingCloseButton
                  ariaLabel="Close updates"
                  className="timeline-drawer-close floating"
                  iconSize={18}
                  onClick={() => setTimelineItem(null)}
                />
                <div className="timeline-drawer-header">
                  <div>
                    <p className="timeline-drawer-title">
                      {timelineItem.product_title}
                    </p>
                    <p className="timeline-drawer-subtitle">
                      {getLineItemCurrentTimeline(timelineItem)?.label ||
                        "Pending"}
                    </p>
                  </div>
                </div>

                <div className="timeline-drawer-info">
                  <InfoOutlinedIcon style={{ width: "16px" }} />
                  <p>
                    You are seeing the same order status information that our
                    customer service associates can access.
                  </p>
                </div>

                {timelineItem.return_shipment_awb &&
                !isCustomerAwb(timelineItem.return_shipment_awb) ? (
                  <div className="timeline-shipment-details">
                    <div className="timeline-shipment-header">
                      <p className="timeline-shipment-title">
                        Shipment Details
                      </p>
                      {timelineItem.return_shipment_status ? (
                        <span
                          className={`timeline-shipment-status-pill ${getShipmentStatusTone(
                            timelineItem.return_shipment_status,
                          )}`}
                        >
                          {formatStatusLabel(
                            timelineItem.return_shipment_status,
                          )}
                        </span>
                      ) : null}
                    </div>
                    <div className="timeline-shipment-grid">
                      <div>
                        <span>Courier</span>
                        <strong>
                          {timelineItem.return_shipment_company || "-"}
                        </strong>
                      </div>
                      <div>
                        <span>AWB Number</span>
                        {timelineItem.return_shipment_url ? (
                          <a
                            href={timelineItem.return_shipment_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {timelineItem.return_shipment_awb}
                            <OpenInNewIcon />
                          </a>
                        ) : (
                          <strong>{timelineItem.return_shipment_awb}</strong>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="timeline-drawer-list">
                  {getTimelineSteps(timelineItem).map((step) => (
                    <div
                      key={step.code}
                      className={`timeline-drawer-row ${
                        step.current ? "current" : ""
                      } ${step.completed ? "completed" : ""} ${
                        step.finalCompleted ? "final-completed" : ""
                      }`}
                    >
                      <span className="timeline-drawer-marker" />
                      <div>
                        <p>{step.label}</p>
                        {step.timestamp ? (
                          <span>{formatTimelineTimestamp(step.timestamp)}</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </StyledTimelineDrawer>
            ) : null}
          </BottomSheetDrawer>

          {/* {merchantConfig.whatsapp_help &&
            merchantConfig.whatsapp_help !== null && (
              <a
                href={merchantConfig.whatsapp_help}
                target="_blank"
                rel="noreferrer"
                className="help"
                style={{
                  color: "#3B444D",
                  padding: "20px 0px",
                  textDecoration: "underline",
                }}
              >
                Need Help?
              </a>
            )} */}
        </StyledTracking>
      )}
    </StyledApp>
  );
}

export default Tracking;
